import React, { ChangeEvent, useState } from 'react'

import Modal from 'react-modal'
import { toast } from 'react-toastify'
import { useChainId, useSignMessage, useSignTypedData } from 'wagmi'

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/24/outline'

import { getSandDollarsRedeemNonce, redeemSandDollars } from '@src/api/sandDollars.api'
import LoadingSpinner from '@src/components/icons/LoadingSpinner'
import { useWaitForTransaction } from '@src/hooks/useWaitForTransaction'
import { Balance } from '@src/utils/balance'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('RedeemModal')

interface RedeemModalProps {
    isOpen: boolean
    onClose: () => void
    address: `0x${string}`
    sandDollarsBalance: Balance
    onRedeemSuccess?: () => Promise<void> | void
}

interface TransactionData {
    success: boolean
    uuid: string
    tx_hash: `0x${string}`
    chain_id: string
}

const RedeemModal: React.FC<RedeemModalProps> = ({ isOpen, onClose, address, sandDollarsBalance, onRedeemSuccess }) => {
    const [amount, setAmount] = useState('')
    const [isRedeeming, setIsRedeeming] = useState(false)
    const [isWaitingForTransaction, setIsWaitingForTransaction] = useState(false)
    const [isWaitingForSignature, setIsWaitingForSignature] = useState(false)
    const [transactionData, setTransactionData] = useState<TransactionData | null>(null)
    const { waitForTransactionReceipt } = useWaitForTransaction()
    const { signTypedDataAsync } = useSignTypedData()

    const EIP712_DOMAIN = {
        name: 'RedeemRequest', // shows up in MetaMask
        version: '1',
    }

    const EIP712_TYPES = {
        Redeem: [
            { name: 'action', type: 'string' },
            { name: 'amount', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'address', type: 'address' },
        ],
    }

    async function signRedeem(amount: bigint, nonce: bigint, address: `0x${string}`) {
        const signature = await signTypedDataAsync({
            domain: EIP712_DOMAIN,
            types: EIP712_TYPES,
            primaryType: 'Redeem',
            message: {
                action: 'redeem',
                amount: amount.toString(), // uint256 – pass bigint
                nonce: nonce.toString(), // uint256 – pass bigint
                address, // checksum address
            },
        })
        if (!signature) throw new Error('Failed to sign the message')

        return { amount, nonce, address, signature }
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // Allow only numbers and decimal point with max 6 decimal places
        if (value === '' || /^[0-9]*(\.[0-9]{0,6})?$/.test(value)) {
            setAmount(value)
        }
    }

    const handleSetMax = () => {
        setAmount(sandDollarsBalance.formatWithCommas(6))
    }

    const handleRedeem = async () => {
        if (!amount || parseFloat(amount) <= 0) {
            toast.error('Please enter a valid amount')
            return
        }

        try {
            setIsRedeeming(true)
            setIsWaitingForSignature(false)

            // Create a new Balance instance for parsing to avoid modifying the parent component's state
            const tempBalance = new Balance({
                name: sandDollarsBalance.name,
                symbol: sandDollarsBalance.symbol,
                bigintValue: BigInt(0),
                decimals: sandDollarsBalance.decimals,
                chainId: sandDollarsBalance.chainId,
                address: sandDollarsBalance.address,
            })

            const parsedAmount = tempBalance.parseString(amount).bigintValue

            if (parsedAmount > sandDollarsBalance.bigintValue) {
                toast.error('Amount exceeds your balance')
                setIsRedeeming(false)
                return
            }

            // Get nonce for redemption
            const { nonce } = await getSandDollarsRedeemNonce(address)

            setIsWaitingForSignature(true)
            const { signature } = await signRedeem(parsedAmount, BigInt(nonce), address)
            setIsWaitingForSignature(false)

            if (!signature) {
                throw new Error('Failed to sign the message')
            }

            // Submit redemption request
            const response = await redeemSandDollars(address, nonce, signature, parsedAmount.toString())

            if (response && typeof response === 'object' && 'success' in response && response.success) {
                // Cast the response to TransactionData type
                const txData = response as TransactionData
                setTransactionData(txData)
                setIsWaitingForTransaction(true)

                logger.info(`[SandDollars] Redeem response: ${JSON.stringify(txData)}`)

                // Wait for transaction receipt
                try {
                    if (onRedeemSuccess) {
                        try {
                            await onRedeemSuccess()
                        } catch (error) {
                            logger.error('Error in onRedeemSuccess callback:', error)
                        }
                    }

                    await waitForTransactionReceipt(
                        txData.tx_hash,
                        false, // not Solana
                        false, // not Tron
                        Number(txData.chain_id) // chain ID
                    )

                    toast.success('Sand Dollars redeemed successfully!')
                    setIsWaitingForTransaction(false)
                } catch (error) {
                    logger.error('Error waiting for transaction receipt:', error)
                    toast.warning('Transaction submitted but confirmation is taking longer than expected')
                    setIsWaitingForTransaction(false)
                }
            } else {
                throw new Error('Redemption failed')
            }
        } catch (error) {
            logger.error('Error redeeming Sand Dollars:', error)
            toast.error('Failed to redeem Sand Dollars. Please try again later.')
        } finally {
            setIsWaitingForSignature(false)
            setIsRedeeming(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={isRedeeming ? () => {} : onClose}
            ariaHideApp={false}
            className={`fixed left-1/2 top-1/2 flex max-h-[calc(100vh-90px)] w-[464px] max-w-[calc(100%-24px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden overflow-y-auto outline-none transition-all duration-500`}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0,0,0,.6)',
                },
            }}
            id='redeem-sand-dollars-modal'
        >
            <div className='space-y-5 rounded-2xl border border-mach-modal-border bg-mach-modal-background p-6 shadow-xl'>
                <div className='mb-4 flex items-center justify-between'>
                    <h2 className='text-lg font-medium'>Redeem Sand Dollars</h2>
                    <button
                        onClick={onClose}
                        disabled={isRedeeming}
                        className='rounded-full p-1 text-gray-400 hover:bg-gray-800 hover:text-white'
                    >
                        <XMarkIcon className='h-6 w-6' />
                    </button>
                </div>

                <div className='mb-4'>
                    <p className='mb-4 text-sm text-gray-400'>Enter the amount of Sand Dollars you want to redeem.</p>

                    <div className='relative mt-3'>
                        <div className='rounded-xl bg-[#1E1E1E] p-4'>
                            <div className='mb-2 flex items-center justify-between'>
                                <label className='text-sm text-gray-400'>Amount</label>
                                <div className='flex items-center gap-2'>
                                    <span className='text-xs text-gray-400'>
                                        Balance: {sandDollarsBalance.formatWithCommas(6)}
                                    </span>
                                    <button
                                        onClick={handleSetMax}
                                        disabled={isRedeeming}
                                        className='rounded bg-[#292929] px-2 py-0.5 text-xs text-white hover:bg-[#333333]'
                                    >
                                        MAX
                                    </button>
                                </div>
                            </div>

                            <div className='relative flex items-center'>
                                <input
                                    type='text'
                                    value={amount}
                                    onChange={handleInputChange}
                                    disabled={isRedeeming}
                                    placeholder='0.0'
                                    className='w-full bg-transparent text-3xl font-semibold text-white outline-none'
                                />
                                <div className='absolute right-0 flex items-center'>
                                    <div className='flex items-center gap-2'>
                                        <span className='text-3xl font-semibold'>SD</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {transactionData && !isWaitingForTransaction ? (
                    <div className='flex flex-col gap-4'>
                        <div className='rounded-lg bg-[#1E1E1E] p-4'>
                            <p className='mb-2 text-sm text-gray-400'>Transaction submitted:</p>
                            <p className='truncate text-sm text-white'>{transactionData.tx_hash}</p>
                        </div>

                        <a
                            href={`${getBlockExplorerUrlFromChainId(transactionData.chain_id)}/${transactionData.tx_hash}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='hover:bg-deep-dark-orange flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-[#FF6200] py-3 font-medium text-white transition-colors'
                        >
                            View Transaction <ArrowTopRightOnSquareIcon className='h-4 w-4' />
                        </a>

                        <button
                            onClick={onClose}
                            className='h-12 w-full rounded-lg border border-gray-600 bg-transparent py-3 font-medium text-white transition-colors hover:bg-gray-800'
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={handleRedeem}
                        disabled={
                            isRedeeming ||
                            isWaitingForTransaction ||
                            isWaitingForSignature ||
                            !amount ||
                            parseFloat(amount) <= 0
                        }
                        className={`h-14 w-full rounded-lg ${
                            isRedeeming ||
                            isWaitingForTransaction ||
                            isWaitingForSignature ||
                            !amount ||
                            parseFloat(amount) <= 0
                                ? 'text-bright-orange cursor-not-allowed bg-[#FF6200]'
                                : 'text-bright-orange hover:bg-deep-dark-orange bg-[#FF6200]'
                        } py-3 font-medium transition-colors`}
                    >
                        {isRedeeming || isWaitingForTransaction || isWaitingForSignature ? (
                            <div className='flex items-center justify-center gap-2'>
                                <LoadingSpinner className='h-5 w-5 animate-spin' color='white' />
                                {isWaitingForSignature
                                    ? 'Waiting for signature...'
                                    : isWaitingForTransaction
                                      ? 'Waiting for confirmation...'
                                      : 'Processing...'}
                            </div>
                        ) : (
                            'Redeem'
                        )}
                    </button>
                )}
            </div>
        </Modal>
    )
}

export default RedeemModal
